// 2026-05-18 SEO L2.2: per-voucher branded landing pages targeting
// the brand-specific gift-voucher queries ("Veluwse Bron cadeau",
// "Disneyland Paris cadeau", "SpaWell cadeaubon", etc.) where the
// generic /vouchers index lacks the keyword density to win.
//
// Architecture mirrors the L2 hubs (_cadeaubon/intent-config.js): one
// config entry per branded landing, shared BrandedVoucherShell renders
// the page. Translation keys live in src/i18n/nl.json under
// cadeaubon.<brand_snake>.* — same NL-only force-locale pattern as L2.
//
// Each entry maps a URL slug to a single voucher (by deals_id), plus
// the i18n namespace. The intro paragraph is REUSED verbatim from
// src/lib/voucher-seo-overrides.js (already-shipped keyword-rich copy
// validated by Google Ads QS-rebound) so no new copy needed for that.

//
// 03-11-2026 (cadeaubonpagina's kloppen met de bon):
//   - `retired: true`: de bon is niet meer te koop. De pagina geeft in de
//     middleware een echte 301 naar `parentHub` (gelokaliseerd, één sprong),
//     en staat niet in de sitemap, de hub of llms.txt (isBrandedLandingLive).
//   - `salesEndDate` (YYYY-MM-DD, Europe/Amsterdam): de laatste verkoopdag.
//     De dag erna geldt het merk als `retired`, zonder deploy.
//   - `categoryId` (bij een `salesEndDate`): de category_id van de bon; de
//     hubs met die categorie tonen zijn kaart (salesEndPaths).
//   - `sitemapPriority`: de prioriteit van de pagina in de sitemap.
// De feiten per bon (board, nachten, hotelplek) staan in
// test/fixtures/cadeaubon-bonfeiten.mjs; de test eist voor elk merk een rij.

import { MARKET_OPTIONS } from "@/lib/site-routing";
import { localizePath } from "@/lib/slug-paths";
import { amsterdamBusinessDate, millisecondsUntilAmsterdamDateChange } from "@/lib/fixed-date-voucher.mjs";
import { INTENT_LANDINGS } from "../_cadeaubon/intent-config";
import { OCCASION_LANDINGS } from "../_cadeaubon/occasion-config";

export const BRANDED_VOUCHER_LANDINGS = {
  "veluwse-bron": {
    slug: "veluwse-bron",
    path: "/cadeaubon/veluwse-bron",
    dealsId: "225",
    voucherSlug: "wellness-veluwse-bron-dagkaart-overnachting-met-ontbijt",
    i18nNs: "cadeaubon.veluwse_bron",
    breadcrumbName: "Veluwse Bron cadeaubon",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.65,
  },
  "disneyland-paris": {
    slug: "disneyland-paris",
    // Fase 2 (2026-07-07): locales with authored landing content (mirrors the
    // sitemap gating); drives per-market hreflang below.
    crossBorderLocales: ["nl", "de", "fr", "en"],
    path: "/cadeaubon/disneyland-paris",
    dealsId: "229",
    voucherSlug: "magische-beleving-disneyland-paris-ticket-hotelovernachting",
    i18nNs: "cadeaubon.disneyland_paris",
    breadcrumbName: "Disneyland Paris cadeaubon",
    parentHub: "/cadeaubon/pretparken",
    parentHubName: "Themapark cadeaubon",
    sitemapPriority: 0.65,
  },
  // 249: de bon is actief maar staat tijdelijk niet in de winkellijst; de
  // shell toont dan geen prijs en verwijst naar de hub.
  spawell: {
    slug: "spawell",
    path: "/cadeaubon/spawell",
    dealsId: "249",
    voucherSlug: "wellness-voucher-spawell-dagkaart-overnachting-met-ontbijt",
    i18nNs: "cadeaubon.spawell",
    breadcrumbName: "SpaWell cadeaubon",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.65,
  },
  "thermen-holiday": {
    slug: "thermen-holiday",
    path: "/cadeaubon/thermen-holiday",
    dealsId: "248",
    voucherSlug:
      "wellness-voucher-thermen-holiday-dagkaart-overnachting-met-ontbijt",
    i18nNs: "cadeaubon.thermen_holiday",
    breadcrumbName: "Thermen Holiday cadeaubon",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.65,
  },
  spaweesp: {
    slug: "spaweesp",
    path: "/cadeaubon/spaweesp",
    dealsId: "247",
    voucherSlug: "wellness-voucher-spaweesp-dagkaart-overnachting-met-ontbijt",
    i18nNs: "cadeaubon.spaweesp",
    breadcrumbName: "SpaWeesp cadeaubon",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.65,
  },
  elysium: {
    slug: "elysium",
    path: "/cadeaubon/elysium",
    dealsId: "246",
    voucherSlug: "wellness-voucher-elysium-dagkaart-overnachting-met-ontbijt",
    i18nNs: "cadeaubon.elysium",
    breadcrumbName: "Elysium cadeaubon",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.65,
  },
  // L4a 2026-05-18: remaining 11 voucher brands. No voucher-seo-overrides.js
  // intros exist for these, so the shell falls back to t(`<ns>.intro`)
  // which lives in i18n.
  spapuur: {
    slug: "spapuur",
    path: "/cadeaubon/spapuur",
    dealsId: "245",
    voucherSlug: "wellness-voucher-spapuur-dagkaart-overnachting-met-ontbijt",
    i18nNs: "cadeaubon.spapuur",
    breadcrumbName: "SpaPuur cadeaubon",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.6,
  },
  // 244 bestaat niet meer (live catalogus 03-11-2026).
  spasense: {
    retired: true,
    slug: "spasense",
    path: "/cadeaubon/spasense",
    dealsId: "244",
    voucherSlug: "wellness-voucher-spasense-dagkaart-overnachting-met-ontbijt",
    i18nNs: "cadeaubon.spasense",
    breadcrumbName: "SpaSense cadeaubon",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.6,
  },
  zwaluwhoeve: {
    slug: "zwaluwhoeve",
    path: "/cadeaubon/zwaluwhoeve",
    dealsId: "181",
    voucherSlug: "wellness-voucher-zwaluwhoeve",
    i18nNs: "cadeaubon.zwaluwhoeve",
    breadcrumbName: "Zwaluwhoeve cadeaubon",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.6,
  },
  "wellness-keuze": {
    slug: "wellness-keuze",
    path: "/cadeaubon/wellness-keuze",
    dealsId: "55",
    voucherSlug: "wellness-voucher-keuze-uit-8-resorts-overnachting-met-ontbijt",
    i18nNs: "cadeaubon.wellness_keuze",
    breadcrumbName: "Wellness voucher (8 resorts)",
    parentHub: "/cadeaubon/wellness",
    parentHubName: "Wellness cadeaubon",
    sitemapPriority: 0.6,
  },
  efteling: {
    slug: "efteling",
    crossBorderLocales: ["nl", "de", "en"],
    path: "/cadeaubon/efteling",
    dealsId: "196",
    voucherSlug: "efteling-plezier-voor-twee-personen-met-toegangstickets-en-hotel-naar-keuze",
    i18nNs: "cadeaubon.efteling",
    breadcrumbName: "Efteling cadeaubon",
    parentHub: "/cadeaubon/pretparken",
    parentHubName: "Themapark cadeaubon",
    sitemapPriority: 0.6,
  },
  "tropical-island": {
    slug: "tropical-island",
    crossBorderLocales: ["nl", "de"],
    path: "/cadeaubon/tropical-island",
    dealsId: "176",
    voucherSlug: "tropical-island-voucher-tickets-resortverblijf",
    i18nNs: "cadeaubon.tropical_island",
    breadcrumbName: "Tropical Island cadeaubon",
    parentHub: "/cadeaubon/pretparken",
    parentHubName: "Themapark cadeaubon",
    sitemapPriority: 0.6,
  },
  phantasialand: {
    slug: "phantasialand",
    crossBorderLocales: ["nl", "de", "en"],
    path: "/cadeaubon/phantasialand",
    dealsId: "92",
    voucherSlug: "phantasialand-special-dagticket-hotel-naar-keuze",
    i18nNs: "cadeaubon.phantasialand",
    breadcrumbName: "Phantasialand cadeaubon",
    parentHub: "/cadeaubon/pretparken",
    parentHubName: "Themapark cadeaubon",
    sitemapPriority: 0.6,
  },
  // 255: actief, maar tijdelijk niet in de winkellijst (zoals 249).
  "theater-londen": {
    slug: "theater-londen",
    // Localized brand slugs live in src/lib/slug-paths.js (theater-london /
    // theatre-londres / london-theatre) so middleware + links share one map.
    crossBorderLocales: ["nl", "de", "fr", "en"],
    path: "/cadeaubon/theater-londen",
    dealsId: "255",
    voucherSlug: "theaterervaring-in-londen-met-harry-potter-en-het-vervloekte-kind",
    i18nNs: "cadeaubon.theater_londen",
    breadcrumbName: "Theater Londen cadeaubon",
    parentHub: "/cadeaubon/stedentrip",
    parentHubName: "Stedentrip cadeaubon",
    sitemapPriority: 0.6,
  },
  // 115, 95 en 128 zijn inactief (live catalogus 03-11-2026).
  "stedentrip-frankfurt": {
    retired: true,
    slug: "stedentrip-frankfurt",
    path: "/cadeaubon/stedentrip-frankfurt",
    dealsId: "115",
    voucherSlug: "stedentrip-voucher-frankfurt-lille-of-brussel",
    i18nNs: "cadeaubon.stedentrip_frankfurt",
    breadcrumbName: "Stedentrip Frankfurt cadeaubon",
    parentHub: "/cadeaubon/stedentrip",
    parentHubName: "Stedentrip cadeaubon",
    sitemapPriority: 0.6,
  },
  "stedentrip-parijs": {
    retired: true,
    slug: "stedentrip-parijs",
    path: "/cadeaubon/stedentrip-parijs",
    dealsId: "95",
    voucherSlug: "stedentrip-voucher-parijs-berlijn-of-antwerpen",
    i18nNs: "cadeaubon.stedentrip_parijs",
    breadcrumbName: "Stedentrip Parijs cadeaubon",
    parentHub: "/cadeaubon/stedentrip",
    parentHubName: "Stedentrip cadeaubon",
    sitemapPriority: 0.6,
  },
  "kasteel-europa": {
    retired: true,
    slug: "kasteel-europa",
    crossBorderLocales: ["nl", "de", "fr", "en"],
    path: "/cadeaubon/kasteel-europa",
    dealsId: "128",
    voucherSlug: "kasteel-voucher-nederland-duitsland-of-frankrijk",
    i18nNs: "cadeaubon.kasteel_europa",
    breadcrumbName: "Kasteel cadeaubon Europa",
    parentHub: "/cadeaubon/weekendje-weg",
    parentHubName: "Weekendje weg cadeau",
    sitemapPriority: 0.6,
  },
  // 03-11-2026: drie nieuwe NL-merkpagina's (zelfde opbouw als de NL-only
  // merken). Hub stedentrip: elk van de drie is een verblijf in een stad.
  // 182 stopt met verkopen op 23-12-2026 (salesEndDate).
  kerstmarkt: {
    slug: "kerstmarkt",
    path: "/cadeaubon/kerstmarkt",
    dealsId: "182",
    voucherSlug: "kerstmarkt-voucher-antwerpen-of-dusseldorf",
    i18nNs: "cadeaubon.kerstmarkt",
    breadcrumbName: "Kerstmarkt cadeaubon",
    parentHub: "/cadeaubon/stedentrip",
    parentHubName: "Stedentrip cadeaubon",
    sitemapPriority: 0.6,
    salesEndDate: "2026-12-23",
    // category_id van de bon: elke hub met deze categorie toont zijn kaart
    // (salesEndPaths).
    categoryId: "city_trip",
  },
  amsterdam: {
    slug: "amsterdam",
    path: "/cadeaubon/amsterdam",
    dealsId: "332",
    voucherSlug: "amsterdam-stedentrip-voucher-1-nacht-grachtenrondvaart-voor-2",
    i18nNs: "cadeaubon.amsterdam",
    breadcrumbName: "Amsterdam cadeaubon",
    parentHub: "/cadeaubon/stedentrip",
    parentHubName: "Stedentrip cadeaubon",
    sitemapPriority: 0.6,
  },
  rome: {
    slug: "rome",
    path: "/cadeaubon/rome",
    dealsId: "333",
    voucherSlug: "rome-stedentrip-voucher-3-dagen-hop-on-hop-off-voor-2",
    i18nNs: "cadeaubon.rome",
    breadcrumbName: "Rome cadeaubon",
    parentHub: "/cadeaubon/stedentrip",
    parentHubName: "Stedentrip cadeaubon",
    sitemapPriority: 0.6,
  },
};

/**
 * Is deze merkpagina vandaag nog te koop? Nee bij `retired`, en bij een
 * `salesEndDate` vanaf de dag erna (Europe/Amsterdam). Eén regel voor de
 * middleware-301, de sitemap, de hubs en llms.txt.
 */
export function isBrandedLandingRetired(brand, now = new Date()) {
  if (!brand) return true;
  if (brand.retired === true) return true;
  if (brand.salesEndDate) {
    const today = amsterdamBusinessDate(now);
    // Een onbruikbare klok verkoopt niets door: dicht.
    return !today || today > brand.salesEndDate;
  }
  return false;
}

/** Eén pensioenregel voor vaste en dynamische voucherregels in llms.txt. */
export function isBrandedVoucherRetired({ brandKey, dealsId, slug }, now = new Date()) {
  return Object.entries(BRANDED_VOUCHER_LANDINGS).some(
    ([key, brand]) =>
      (key === brandKey || brand.dealsId === String(dealsId) || brand.voucherSlug === slug) &&
      isBrandedLandingRetired(brand, now),
  );
}

/**
 * Fixronde 2 #1920: staat deze bon (`deals_id`) vandaag uit de verkoop volgens
 * de config (`retired`, of na zijn salesEndDate)? Los van wat de backend zegt:
 * na 23-12-2026 geeft de backend 182 mogelijk nog als actief terug, maar dan
 * is hij niet meer te koop (checkout, voucherpagina, hub- en
 * gelegenheidskaarten).
 */
export function isVoucherSaleClosed(dealsId, now = new Date()) {
  if (dealsId === undefined || dealsId === null || dealsId === "") return false;
  return Object.values(BRANDED_VOUCHER_LANDINGS).some(
    (brand) => brand.dealsId === String(dealsId) && isBrandedLandingRetired(brand, now),
  );
}

/**
 * `vouchers` zonder de bonnen die op `now` niet meer te koop zijn
 * (isVoucherSaleClosed). Fixronde 3 #1920: de ene gedeelde filter voor elke
 * lijst die een bon kan tonen (hubs, categorie-, thema-, onderwerp- en
 * bestemmingspagina's, homepage, zoeken, aanbevelingen, feeds, llms.txt).
 * Geef de klok van de render of van het antwoord mee.
 */
export function withoutClosedVouchers(vouchers, now = new Date()) {
  return (Array.isArray(vouchers) ? vouchers : []).filter(
    (v) => !isVoucherSaleClosed(v?.deals_id ?? v?.dealId ?? v?.id, now),
  );
}

/**
 * Fixronde 3 #1920: heeft deze rij een merk met een laatste verkoopdag
 * (`salesEndDate`, niet vast `retired`)? Dan moet een lijst die hem toont zijn
 * klok op de Amsterdamse middernacht zetten (client) of zijn cache daar laten
 * eindigen (server).
 */
export function hasVoucherSalesEnd(row) {
  const id = row?.deals_id ?? row?.dealId ?? row?.id;
  if (id === undefined || id === null || id === "") return false;
  return Object.values(BRANDED_VOUCHER_LANDINGS).some(
    (brand) => brand.retired !== true && Boolean(brand.salesEndDate) && brand.dealsId === String(id),
  );
}

/** De bestaande Amsterdamse campagnegrens, ook voor de catalogusdatacaches. */
export function brandedSalesCacheExpiresAt(ttlMs, now = new Date()) {
  if (!Number.isFinite(now.getTime())) return 0;
  return now.getTime() + (isBrandedSalesEndSensitivePath("/llms.txt", now)
    ? Math.min(ttlMs, millisecondsUntilAmsterdamDateChange(now))
    : ttlMs);
}

/**
 * Fixronde 2 #1920 (kop 1af9c837): elk adres dat de bon van een merk met een
 * `salesEndDate` toont. De merkpagina, de hub die hem linkt (`parentHub`),
 * elke hub waarvan de categorie de bon meeneemt (`categoryId`; voor 182 ook
 * /cadeaubon/weekendje-weg), elke gelegenheid die zijn deals_id noemt, het
 * voucheroverzicht, de voucherpagina zelf en /llms.txt.
 */
export function salesEndPaths(brand) {
  if (!brand?.salesEndDate) return [];
  const hubs = Object.values(INTENT_LANDINGS)
    .filter((hub) => brand.categoryId && (hub.categoryFilter || []).includes(brand.categoryId))
    .map((hub) => hub.path);
  const occasions = Object.values(OCCASION_LANDINGS)
    .filter((occasion) => (occasion.dealsIds || []).includes(brand.dealsId))
    .map((occasion) => occasion.path || `/cadeaubon/${occasion.slug}`);
  return [
    brand.path,
    brand.parentHub,
    ...hubs,
    ...occasions,
    "/vouchers",
    `/vouchers/${brand.voucherSlug}`,
    `/vouchers/${brand.dealsId}`,
    "/llms.txt",
  ]
    .filter(Boolean)
    .map((p) => p.toLowerCase());
}

/**
 * Fixronde 3 #1920: de lijsten buiten de cadeaubonpagina's die elke bon kunnen
 * tonen, ook een met een `salesEndDate`: de homepage (voucherterugval en
 * carrousel), /packages/<categorie> (en de gerelateerde deals op een
 * dealpagina), /thema/*, /onderwerpen/*, de bestemmingspagina's en elke
 * voucherpagina (gerelateerde vouchers). Welke bon zo'n antwoord toont, ziet de
 * middleware niet; daarom geldt de cachegrens voor de hele familie, en alleen
 * zolang een merk met een `salesEndDate` nog verkoopt.
 */
const SALES_END_LISTING_SEGMENTS = Object.freeze([
  "packages",
  "thema",
  "onderwerpen",
  "bestemmingen",
  "reiseziele",
  "destinations",
  "vouchers",
]);

export function isSalesEndListingPath(path) {
  if (path === "/") return true;
  const [first] = String(path || "").toLowerCase().split("/").filter(Boolean);
  return SALES_END_LISTING_SEGMENTS.includes(first);
}

/**
 * Kan het antwoord op `physicalPath` een merk tonen dat een `salesEndDate`
 * heeft en vandaag (Europe/Amsterdam) nog verkoopt? Zo'n antwoord mag een
 * gedeelde cache niet over de eerstvolgende Amsterdamse middernacht heen
 * bewaren: de dag na de laatste verkoopdag is het merk weg (301, niet meer in
 * de hub of llms.txt). De adressen: salesEndPaths. De sitemap loopt niet door
 * de middleware en wordt niet gedeeld gecachet. Een onbruikbare klok kan niet
 * bewijzen dat de verkoopstop nog niet voorbij is: dan true, zodat de cache
 * dichtgaat.
 * Gebruikt door middleware.js (withCacheControl) en de llms.txt-route, met
 * clampCacheHeaderToBusinessBoundary uit @/lib/edge-cache.
 */
export function isBrandedSalesEndSensitivePath(physicalPath, now = new Date()) {
  const raw = String(physicalPath || "").toLowerCase();
  const path = raw.length > 1 ? raw.replace(/\/+$/, "") : raw;
  const brands = Object.values(BRANDED_VOUCHER_LANDINGS).filter(
    (brand) =>
      brand.retired !== true &&
      Boolean(brand.salesEndDate) &&
      (salesEndPaths(brand).includes(path) || isSalesEndListingPath(path)),
  );
  // Alleen voor die adressen de klok lezen (middleware vraagt dit per verzoek).
  if (brands.length === 0) return false;
  const today = amsterdamBusinessDate(now);
  return brands.some((brand) => !today || today <= brand.salesEndDate);
}

/** De talen waarin een merkpagina eigen tekst heeft (zonder: alleen nl). */
export function brandedLandingLocales(brand) {
  return Array.isArray(brand?.crossBorderLocales) ? brand.crossBorderLocales : ["nl"];
}

/**
 * De merkpagina's die op een markt met `locale` openbaar zijn: niet
 * gepensioneerd en met tekst in die taal. Bron voor sitemap, hubs en llms.txt.
 */
export function liveBrandedLandings(locale, now = new Date()) {
  return Object.values(BRANDED_VOUCHER_LANDINGS).filter(
    (brand) =>
      !isBrandedLandingRetired(brand, now) &&
      brandedLandingLocales(brand).includes(locale),
  );
}

/**
 * Het doel van de 301 voor een gepensioneerde merkpagina, of null. `physicalPath`
 * is het Nederlandse routepad (/cadeaubon/<merk>, na toPhysicalPath); het doel
 * is de hub in de taal van de markt, zodat het één sprong blijft.
 */
export function retiredBrandedLandingRedirect(physicalPath, locale, now = new Date()) {
  const path = String(physicalPath || "").replace(/\/+$/, "");
  const brand = Object.values(BRANDED_VOUCHER_LANDINGS).find((b) => b.path === path);
  if (!brand || !isBrandedLandingRetired(brand, now)) return null;
  return localizePath(brand.parentHub, locale);
}

/**
 * De voucher-slugs van de merken die op `now` gepensioneerd zijn (`retired`, of
 * na hun salesEndDate). Voor lijsten die een voucherpagina bij naam noemen
 * (llms.txt), zodat zo'n lijst de config volgt.
 */
export function retiredBrandedVoucherSlugs(now = new Date()) {
  return new Set(
    Object.values(BRANDED_VOUCHER_LANDINGS)
      .filter((brand) => isBrandedLandingRetired(brand, now))
      .map((brand) => brand.voucherSlug),
  );
}

/** De deal-id's van bonnen die vast gepensioneerd zijn (`retired: true`). */
export const RETIRED_VOUCHER_DEAL_IDS = new Set(
  Object.values(BRANDED_VOUCHER_LANDINGS)
    .filter((brand) => brand.retired === true)
    .map((brand) => brand.dealsId),
);

// Non-NL TLDs get redirected to /vouchers via hreflang (same pattern as
// L2 hubs). nl-be serves the same Dutch content. Still used verbatim by the
// 15 NL-only branded landings (Veluwse Bron, Spawell, ...), whose brand names
// have no DE/FR/EN equivalent.
export const BRANDED_VOUCHER_ALTERNATE_PATHS = {
  "de-de": "/vouchers",
  "fr-fr": "/vouchers",
  "fr-be": "/vouchers",
  "en-gb": "/vouchers",
};

/**
 * Fase 2 localized URLs (2026-07-07): per-brand hreflang for the 6
 * cross-border landings. Markets whose locale HAS authored content
 * (brand.crossBorderLocales) point at their own localized brand URL
 * (/gutschein/theater-london on .de) instead of the generic /vouchers index;
 * markets without content keep pointing at the voucher index, but now at its
 * market-canonical localized slug (/gutscheine, /bons-cadeaux, /vouchery) so
 * the hreflang target never bounces through a middleware 301.
 *
 * Brands without crossBorderLocales (the 15 NL-only landings) fall back to
 * the legacy shared map above, byte-identical to before.
 */
export function brandedVoucherAlternatePaths(brand) {
  if (!brand?.crossBorderLocales) return BRANDED_VOUCHER_ALTERNATE_PATHS;
  const out = {};
  for (const market of MARKET_OPTIONS) {
    if (market.locale === "nl") continue; // auto-localization = brand.path
    out[market.key] = brand.crossBorderLocales.includes(market.locale)
      ? localizePath(brand.path, market.locale)
      : localizePath("/vouchers", market.locale);
  }
  return out;
}

export function findVoucherForBrand(vouchers, brand) {
  if (!Array.isArray(vouchers) || !brand) return null;
  return (
    vouchers.find((v) => String(v?.deals_id) === brand.dealsId) || null
  );
}
